import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import {
  isGroupLeague, defaultStagePhase, prioritiesForNewStages, nameNumbersForNewStages,
} from '../utilities/stagePriority';
import StagePhasePicker from './StagePhasePicker';
import '../styles/createMatchModal.css';

// Несколько туров одной транзакцией (addStages). В лиге с группами админ
// выбирает этап; номера считает utilities/stagePriority, названия — по счёту
// внутри этапа («4 Тур», «5 Тур»)
function CreateMultipleStagesModal({ isOpen, onClose, onSuccess, league, season, stages: seasonStages = [], format = null }) {
  const dispatch = useDispatch();

  const [phase, setPhase] = useState('group');
  useEffect(() => {
    if (isOpen) setPhase(defaultStagePhase(seasonStages, format));
  }, [isOpen, seasonStages, format]);
  const [stagesAmount, setStagesAmount] = useState('1');
  const [baseNamePrefix, setBaseNamePrefix] = useState('Тур');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amount = parseInt(stagesAmount);
    if (!amount || amount < 1 || amount > 50) {
      setMessage({ text: 'Введите количество от 1 до 50', type: 'error' });
      return;
    }

    if (!baseNamePrefix.trim()) {
      setMessage({ text: 'Введите базовое название', type: 'error' });
      return;
    }
    if (!format) {
      setMessage({ text: 'Настройки сезона ещё не загрузились — попробуйте через секунду', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // Одна транзакция: POST /leagues/:id/addStages (FRONTEND_API_CHANGES §2.5)
      const priorities = prioritiesForNewStages(seasonStages, format, phase, amount);
      const numbers = nameNumbersForNewStages(seasonStages, format, phase, amount);
      const stages = priorities.map((priority, i) => ({
        name: `${numbers[i]} ${baseNamePrefix.trim()}`,
        short_name: null,
        priority,
        group_id: null,
      }));
      setMessage({ text: `Создаётся ${amount} стадий...`, type: 'success' });
      await axios.post(
        BASE_URL.url + `/leagues/${league.value}/addStages`,
        {
          season_id: parseInt(season.value),
          stages,
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      setMessage({ text: `Успешно создано ${amount} стадий!`, type: 'success' });
      setTimeout(() => {
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating stages:', error);
      const data = error.response?.data;
      const reason = (typeof data === 'string' && data)
        || data?.message
        || data?.error
        || 'Ошибка при создании стадий';
      setMessage({ text: reason, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStagesAmount('1');
    setBaseNamePrefix('Тур');
    setMessage({ text: '', type: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Создание нескольких стадий</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-section-modal">
              <label className="form-label-modal form-label-required">
                Количество стадий
              </label>
              <input
                className="input-field-modal"
                type="number"
                value={stagesAmount}
                onChange={(e) => setStagesAmount(e.target.value)}
                min="1"
                max="50"
              />
              <small style={{ color: '#718096', fontSize: '13px', marginTop: '4px' }}>
                {!format
                  ? 'Загружаем настройки сезона…'
                  : isGroupLeague(format)
                    ? (phase === 'group'
                      ? 'Все туры пойдут в групповой этап.'
                      : 'Все туры пойдут в плей-офф.')
                    : 'Туры продолжат нумерацию сезона.'}
              </small>
            </div>

            <StagePhasePicker format={format} phase={phase} onChange={setPhase} disabled={loading} />

            <div className="form-section-modal">
              <label className="form-label-modal form-label-required">
                Базовое название
              </label>
              <input
                className="input-field-modal"
                type="text"
                value={baseNamePrefix}
                onChange={(e) => setBaseNamePrefix(e.target.value)}
                placeholder="Тур"
                maxLength={50}
              />
              <small style={{ color: '#718096', fontSize: '13px', marginTop: '4px' }}>
                {(() => {
                  const numbers = nameNumbersForNewStages(seasonStages, format, phase, 2);
                  return `Стадии будут названы: «${numbers[0]} ${baseNamePrefix}», «${numbers[1]} ${baseNamePrefix}» и т.д.`;
                })()}
              </small>
            </div>

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading || !format}
                className="btn-modal btn-primary-modal"
              >
                {loading ? 'Создание...' : `Создать ${stagesAmount} стадий`}
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateMultipleStagesModal;
